/**
 * 邮箱验证码服务
 */

import axios from 'axios';
import { getApiBaseUrl } from '../utils/config';

// 动态获取API基础URL
const API_BASE_URL = getApiBaseUrl();

class VerificationService {
  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000, // 30秒超时
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // 添加请求拦截器
    this.client.interceptors.request.use(
      (config) => {
        console.log('发送验证码请求:', config.url, config.data);
        return config;
      },
      (error) => {
        console.error('请求错误:', error);
        return Promise.reject(error);
      }
    );

    // 添加响应拦截器
    this.client.interceptors.response.use(
      (response) => {
        console.log('验证码响应:', response.data);
        return response;
      },
      (error) => {
        console.error('响应错误:', error.response?.data || error.message);
        return Promise.reject(error);
      }
    );
  }

  /**
   * 发送邮箱验证码
   * @param {string} email - 邮箱地址
   * @param {string} userName - 用户名称
   * @returns {Promise<Object>} 发送结果
   */
  async sendVerificationCode(email, userName = null) {
    try {
      const response = await this.client.post('/api/v1/verification/send-code', {
        email: email,
        user_name: userName || email.split('@')[0]
      });

      if (response.data.success) {
        return {
          success: true,
          message: response.data.message,
          expireMinutes: response.data.expire_minutes
        };
      } else {
        return {
          success: false,
          error: response.data.error || '发送验证码失败'
        };
      }
    } catch (error) {
      console.error('发送验证码失败:', error);
      
      // 处理不同类型的错误
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'object' && detail.error) {
          return {
            success: false,
            error: detail.error,
            errorCode: detail.error_code
          };
        } else if (typeof detail === 'string') {
          return {
            success: false,
            error: detail
          };
        }
      }

      if (error.code === 'ECONNABORTED') {
        return {
          success: false,
          error: '请求超时，请检查网络连接'
        };
      }

      if (error.response?.status === 500) {
        return {
          success: false,
          error: '服务器内部错误，请稍后重试'
        };
      }

      return {
        success: false,
        error: error.message || '网络连接失败，请检查网络设置'
      };
    }
  }

  /**
   * 验证邮箱验证码
   * @param {string} email - 邮箱地址
   * @param {string} code - 验证码
   * @returns {Promise<Object>} 验证结果
   */
  async verifyCode(email, code) {
    try {
      const response = await this.client.post('/api/v1/verification/verify-code', {
        email: email,
        code: code
      });

      if (response.data.success) {
        return {
          success: true,
          message: response.data.message
        };
      } else {
        return {
          success: false,
          error: response.data.error || '验证码验证失败'
        };
      }
    } catch (error) {
      console.error('验证码验证失败:', error);

      // 处理不同类型的错误
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'object' && detail.error) {
          return {
            success: false,
            error: detail.error,
            errorCode: detail.error_code,
            remainingAttempts: detail.remaining_attempts
          };
        } else if (typeof detail === 'string') {
          return {
            success: false,
            error: detail
          };
        }
      }

      return {
        success: false,
        error: error.message || '验证码验证失败'
      };
    }
  }

  /**
   * 重新发送验证码
   * @param {string} email - 邮箱地址
   * @param {string} userName - 用户名称
   * @returns {Promise<Object>} 发送结果
   */
  async resendVerificationCode(email, userName = null) {
    try {
      const response = await this.client.post('/api/v1/verification/resend-code', {
        email: email,
        user_name: userName || email.split('@')[0]
      });

      return {
        success: response.data.success,
        message: response.data.message,
        error: response.data.error
      };
    } catch (error) {
      console.error('重新发送验证码失败:', error);
      return {
        success: false,
        error: error.message || '重新发送验证码失败'
      };
    }
  }

  /**
   * 检查验证码服务健康状态
   * @returns {Promise<Object>} 健康状态
   */
  async checkHealth() {
    try {
      const response = await this.client.get('/api/v1/verification/health');
      return response.data;
    } catch (error) {
      console.error('健康检查失败:', error);
      return {
        status: 'error',
        message: '验证码服务不可用'
      };
    }
  }
}

// 创建单例实例
export const verificationService = new VerificationService();

// 默认导出
export default verificationService;
